import type { ReactElement, ReactNode } from 'react';

export type TooltipDirection =
  | 'bottom'
  | 'left'
  | 'right'
  | 'top'
  | 'top-end'
  | 'top-start';

export type TooltipTriggerElement = 'div' | 'span';

const PHRASING_ELEMENTS = new Set<string>([
  'a',
  'cite',
  'em',
  's',
  'small',
  'span',
  'strong',
]);

/** Picks `span` vs `div` for the trigger wrapper from the child element type. */
export const resolveTooltipTriggerAs = (
  childType: unknown,
): TooltipTriggerElement => {
  if (typeof childType === 'string' && PHRASING_ELEMENTS.has(childType)) {
    return 'span';
  }
  return 'div';
};

export interface TooltipProps {
  /**
   * A single focusable element that triggers the tooltip on hover or focus.
   * Must be a focusable element (e.g. `Button`, `Link`) so keyboard users
   * can reach it. The tooltip's id is merged onto this element via
   * `aria-describedby` so screen readers announce the description when
   * the trigger receives focus.
   */
  children: ReactElement;
  /** The direction of the tooltip relative to the trigger element. Defaults to 'top'. */
  direction?: TooltipDirection;
  /**
   * Tooltip content. Usually plain text; pass a richer node for per-line
   * formatting. If not provided, the tooltip will not be displayed.
   */
  tooltip?: ReactNode;
  /** EDS-internal only — never set from src/**, only passed sibling-to-sibling within packages/eds. */
  __private?: {
    /**
     * Keeps `flip` on the axis `direction` names rather than letting the
     * tooltip fall back to the cross axis. Set it where a cross-axis
     * placement would cover sibling content — menu rows, for example, where
     * a top/bottom tooltip lands on the adjacent item.
     */
    lockPlacementAxis?: boolean;
    /**
     * When true, always render the trigger wrapper even if `tooltip` is absent.
     * Keeps layout stable while overflow is measured asynchronously.
     */
    wrapTrigger?: boolean;
  };
}
